import { useState, useEffect } from 'react';
import { Box, Tab, Tabs, Typography, CircularProgress, useTheme } from '@mui/material';
import WrapperLineComponent from '@/components/shared/charts/LineComponent/wrapper-line-component';
import CustomModal from '@/DesignSystem/components/custom-modal/CustomModal';
import axiosApiInstance from '@/api/shared/axios-instance';
import URL_FACTORY from '@/helpers/url-helper';
import { currentSeason } from '@/constants/site';
import { translate } from '@/data/fixture-transalations';
import useTeam from '@/hooks/use-team';
import { getTeamImageById } from '@/helpers/images-helper';

const EvolucionPartidosModal = ({ open, onClose, teamId }) => {
    const [selectedTab, setSelectedTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [partidosData, setPartidosData] = useState(null);
    const [rivalLogos, setRivalLogos] = useState([]);

    const { teamName } = useTeam(teamId);
    const theme = useTheme();

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    useEffect(() => {
        if (open && teamId) {
            fetchEvolucionData();
        }
    }, [open, teamId]);

    const fetchEvolucionData = async () => {
        try {
            setLoading(true);
            const response = await axiosApiInstance.post(URL_FACTORY.ObtenerEvolucionEquipo, {
                teamId: teamId,
                season: currentSeason,
            });

            if (response.data && response.data.partidos) {
                const processed = processPartidosData(response.data.partidos);
                setPartidosData(processed);
                loadRivalLogos(response.data.partidos);
            }
        } catch (error) {
            console.error('Error fetching evolución data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadRivalLogos = async (partidos) => {
        const logos = [];

        for (const partido of partidos) {
            const esLocal = partido.home.id === teamId;
            const rivalId = esLocal ? partido.away.id : partido.home.id;

            try {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                const imageUrl = getTeamImageById(rivalId, 30, 30);

                await new Promise((resolve, reject) => {
                    img.onload = () => resolve();
                    img.onerror = () => reject();
                    img.src = imageUrl;
                });

                logos.push(img);
            } catch (error) {
                console.error(`Error loading logo for team ${rivalId}:`, error);
                logos.push('circle'); // Fallback a círculo
            }
        }

        setRivalLogos(logos);
    };

    const processPartidosData = (partidos) => {
        // Procesar datos para las gráficas
        const labels = partidos.map((p) => translate(p.round, true));

        // Calcular puntos por partido (3 victoria, 1 empate, 0 derrota)
        const puntosPorPartido = partidos.map((partido) => {
            const esLocal = partido.home.id === teamId;
            const golesEquipo = esLocal ? partido.home.goles : partido.away.goles;
            const golesRival = esLocal ? partido.away.goles : partido.home.goles;

            if (golesEquipo > golesRival) return 3; // Victoria
            if (golesEquipo === golesRival) return 1; // Empate
            return 0; // Derrota
        });

        // Goles a favor
        const golesAFavor = partidos.map((partido) => {
            const esLocal = partido.home.id === teamId;
            return esLocal ? partido.home.goles : partido.away.goles;
        });

        // Goles en contra
        const golesEnContra = partidos.map((partido) => {
            const esLocal = partido.home.id === teamId;
            return esLocal ? partido.away.goles : partido.home.goles;
        });

        return {
            labels,
            puntosPorPartido,
            golesAFavor,
            golesEnContra,
        };
    };

    // Datos para la gráfica de puntos por jornada
    const getPartidosChartData = () => {
        if (!partidosData) return null;
        const hasLogos = rivalLogos.length > 0;
        return {
            labels: partidosData.labels,
            datasets: [
                {
                    label: 'Puntos por Jornada',
                    data: partidosData.puntosPorPartido,
                    borderColor: '#2196F3',
                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                    borderWidth: 3,
                    tension: 0.1,
                    pointStyle: hasLogos ? rivalLogos : 'circle',
                    pointRadius: hasLogos ? 15 : 6,
                    pointHoverRadius: hasLogos ? 18 : 8,
                    pointBackgroundColor: '#2196F3',
                    pointBorderColor: '#fff',
                    pointBorderWidth: 2,
                },
            ],
        };
    };

    // Datos para goles a favor
    const getGolesAFavorChartData = () => {
        if (!partidosData) return null;
        const hasLogos = rivalLogos.length > 0;
        return {
            labels: partidosData.labels,
            datasets: [
                {
                    label: 'Goles a Favor',
                    data: partidosData.golesAFavor,
                    borderColor: '#4CAF50',
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    borderWidth: 3,
                    tension: 0.1,
                    pointStyle: hasLogos ? rivalLogos : 'circle',
                    pointRadius: hasLogos ? 15 : 6,
                    pointHoverRadius: hasLogos ? 18 : 8,
                    pointBackgroundColor: '#4CAF50',
                    pointBorderColor: '#fff',
                    pointBorderWidth: 2,
                },
            ],
        };
    };

    // Datos para goles en contra
    const getGolesEnContraChartData = () => {
        if (!partidosData) return null;
        const hasLogos = rivalLogos.length > 0;
        return {
            labels: partidosData.labels,
            datasets: [
                {
                    label: 'Goles en Contra',
                    data: partidosData.golesEnContra,
                    borderColor: '#f44336',
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    borderWidth: 3,
                    tension: 0.1,
                    pointStyle: hasLogos ? rivalLogos : 'circle',
                    pointRadius: hasLogos ? 15 : 6,
                    pointHoverRadius: hasLogos ? 18 : 8,
                    pointBackgroundColor: '#f44336',
                    pointBorderColor: '#fff',
                    pointBorderWidth: 2,
                },
            ],
        };
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
            },
        },
        animation: {
            duration: 2000,
            easing: 'easeInOutQuart',
            delay: (context) => {
                let delay = 0;
                if (context.type === 'data' && context.mode === 'default') {
                    delay = context.dataIndex * 200;
                }
                return delay;
            },
        },
        plugins: {
            legend: {
                display: true,
                position: 'top',
                labels: {
                    color: theme.palette.text.primary,
                    font: {
                        size: 14,
                        weight: '500',
                    },
                    padding: 15,
                },
            },
            tooltip: {
                backgroundColor:
                    theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                titleColor: theme.palette.text.primary,
                bodyColor: theme.palette.text.primary,
                borderColor: '#a126da',
                borderWidth: 2,
                padding: 12,
                displayColors: true,
                cornerRadius: 8,
            },
        },
        elements: {
            point: {
                clip: false,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                max: selectedTab === 0 ? 4 : undefined,
                min: selectedTab === 0 ? -0.5 : undefined,
                ticks: {
                    color: theme.palette.text.primary,
                    stepSize: selectedTab === 0 ? 1 : undefined,
                    font: {
                        size: 12,
                    },
                    callback: function (value) {
                        if (selectedTab === 0) {
                            // Para el tab de partidos
                            if (value === 0) return 'Derrota (0)';
                            if (value === 1) return 'Empate (1)';
                            if (value === 3) return 'Victoria (3)';
                            return ''; // No mostrar otros valores (como 4)
                        }
                        return value;
                    },
                },
                grid: {
                    color:
                        theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                    lineWidth: 1,
                },
            },
            x: {
                ticks: {
                    color: theme.palette.text.primary,
                    font: {
                        size: 12,
                    },
                },
                grid: {
                    color:
                        theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                    lineWidth: 1,
                },
            },
        },
    };

    const getChartData = () => {
        switch (selectedTab) {
            case 0:
                return getPartidosChartData();
            case 1:
                return getGolesAFavorChartData();
            case 2:
                return getGolesEnContraChartData();
            default:
                return getPartidosChartData();
        }
    };

    return (
        <CustomModal
            open={open}
            onClose={onClose}
            title={`Evolución - ${teamName}`}
            maxWidth="md"
            width="98%"
            fullWidth
            paperSx={{
                minHeight: '550px',
                maxWidth: '900px',
            }}
            contentSx={{ pt: 2 }}
        >
            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '400px',
                    }}
                >
                    <CircularProgress sx={{ color: '#a126da' }} />
                </Box>
            ) : (
                <>
                    <Box sx={{ borderBottom: 1, borderColor: 'border.main', mb: 1 }}>
                        <Tabs
                            value={selectedTab}
                            onChange={handleTabChange}
                            sx={{
                                minHeight: '40px',
                                '& .MuiTab-root': {
                                    color: 'text.secondary',
                                    minHeight: '40px',
                                    padding: '8px 16px',
                                    '&.Mui-selected': {
                                        color: '#a126da',
                                    },
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#a126da',
                                },
                            }}
                        >
                            <Tab label="Partidos" />
                            <Tab label="Goles a Favor" />
                            <Tab label="Goles en Contra" />
                        </Tabs>
                    </Box>

                    {partidosData && getChartData() ? (
                        <>
                            <Box sx={{ height: '400px', width: '100%', mb: 3 }}>
                                <WrapperLineComponent
                                    key={`chart-${selectedTab}-${rivalLogos.length}`}
                                    data={getChartData()}
                                    options={chartOptions}
                                    shouldLoad={open && !loading}
                                />
                            </Box>

                            {selectedTab === 0 && (
                                <Box
                                    sx={{ p: 2, backgroundColor: 'rgba(33, 150, 243, 0.1)', borderRadius: 1 }}
                                >
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        💡 Esta gráfica muestra los puntos obtenidos por jornada: 3 puntos por
                                        victoria, 1 punto por empate, 0 puntos por derrota.
                                    </Typography>
                                </Box>
                            )}

                            {selectedTab === 1 && (
                                <Box
                                    sx={{ p: 2, backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1 }}
                                >
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        ⚽ Goles marcados por el equipo en cada jornada de la temporada.
                                    </Typography>
                                </Box>
                            )}

                            {selectedTab === 2 && (
                                <Box
                                    sx={{ p: 2, backgroundColor: 'rgba(244, 67, 54, 0.1)', borderRadius: 1 }}
                                >
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        🥅 Goles recibidos por el equipo en cada jornada de la temporada.
                                    </Typography>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '400px',
                            }}
                        >
                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                No hay datos disponibles
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </CustomModal>
    );
};

export default EvolucionPartidosModal;
